import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createService } from '../services/api';
import {
  SERVICE_CATEGORIES,
  INDIAN_STATES,
  SPECIALTIES,
  LANGUAGES,
  AGE_GROUPS,
  TARGET_POPULATIONS,
  TREATMENT_APPROACHES,
  COST_OPTIONS,
  MODE_OPTIONS,
} from '../lib/constants';

const RegisterService = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    service_types: [],
    state: '',
    city: '',
    pincode: '',
    contact_phone: '',
    cost: '',
    availability_hours: '',
    mode: [],
    email: '',
    website: '',
    specialties: [],
    languages: [],
    age_groups: [],
    target_populations: [],
    certifications: '',
    treatment_approaches: [],
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Login Required</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to register a service.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMultiSelect = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter((v) => v !== value) });
    } else {
      setFormData({ ...formData, [field]: [...current, value] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await createService(formData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/my-services');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to register service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Service Registered Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Your service has been submitted for review. You'll be notified once it's approved.
          </p>
          <p className="text-sm text-blue-600 font-medium">Redirecting to My Services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="register-service-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="register-service-title">
            Register Your Mental Health Service
          </h1>
          <p className="text-gray-600 mb-8">
            Fill in the details below. All services go through a manual approval process before being published.
          </p>

          {/* What happens next explainer */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <h3 className="font-semibold text-blue-900 mb-2">💡 What happens next?</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Your service will be reviewed by our team (usually within 24-48 hours)</li>
              <li>• You'll be able to see the status in "My Services" dashboard</li>
              <li>• Once approved, your service will be visible to people seeking help</li>
              <li>• If rejected, you'll receive clear feedback on what needs improvement</li>
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6" data-testid="register-service-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Mandatory Fields */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">Basic Information (Required)</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="service-name-input"
                    placeholder="e.g., Hope Counselling Center"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description * (Use simple, non-clinical language)
                  </label>
                  <textarea
                    name="description"
                    required
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="service-description-input"
                    placeholder="Describe your service in plain language that anyone can understand..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Types * (Select all that apply)
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {SERVICE_CATEGORIES.map((category) => (
                      <label key={category} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.service_types.includes(category)}
                          onChange={() => handleMultiSelect('service_types', category)}
                          className="mr-2"
                          data-testid="service-type-checkbox"
                        />
                        <span className="text-sm">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <select
                      name="state"
                      required
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="service-state-select"
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="service-city-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                    <input
                      type="text"
                      name="pincode"
                      required
                      pattern="[0-9]{6}"
                      value={formData.pincode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="service-pincode-input"
                      placeholder="6 digits"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone * (Primary contact number)
                  </label>
                  <input
                    type="tel"
                    name="contact_phone"
                    required
                    value={formData.contact_phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="service-phone-input"
                    placeholder="10 digit mobile number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost *</label>
                  <select
                    name="cost"
                    required
                    value={formData.cost}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="service-cost-select"
                  >
                    <option value="">Select cost option</option>
                    {COST_OPTIONS.map((cost) => (
                      <option key={cost} value={cost}>
                        {cost}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Availability Hours *
                  </label>
                  <input
                    type="text"
                    name="availability_hours"
                    required
                    value={formData.availability_hours}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="service-hours-input"
                    placeholder="e.g., Mon-Fri 9AM-6PM, 24/7"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Mode * (Select all that apply)
                  </label>
                  <div className="flex gap-4">
                    {MODE_OPTIONS.map((mode) => (
                      <label key={mode} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.mode.includes(mode)}
                          onChange={() => handleMultiSelect('mode', mode)}
                          className="mr-2"
                          data-testid="service-mode-checkbox"
                        />
                        <span className="text-sm capitalize">{mode}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Optional Fields - Progressive Disclosure */}
            <div>
              <button
                type="button"
                onClick={() => setShowOptional(!showOptional)}
                className="text-blue-600 hover:text-blue-700 font-medium mb-4"
                data-testid="toggle-optional-fields"
              >
                {showOptional ? '▼ Hide' : '▶'} Optional Fields (Recommended for better visibility)
              </button>

              {showOptional && (
                <div className="space-y-4 border-t pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specialties</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                      {SPECIALTIES.map((specialty) => (
                        <label key={specialty} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.specialties.includes(specialty)}
                            onChange={() => handleMultiSelect('specialties', specialty)}
                            className="mr-2"
                          />
                          <span className="text-sm">{specialty}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Languages Spoken</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                      {LANGUAGES.map((language) => (
                        <label key={language} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.languages.includes(language)}
                            onChange={() => handleMultiSelect('languages', language)}
                            className="mr-2"
                          />
                          <span className="text-sm">{language}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Age Groups Served</label>
                    <div className="grid grid-cols-2 gap-2">
                      {AGE_GROUPS.map((age) => (
                        <label key={age} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.age_groups.includes(age)}
                            onChange={() => handleMultiSelect('age_groups', age)}
                            className="mr-2"
                          />
                          <span className="text-sm">{age}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target Populations</label>
                    <div className="grid grid-cols-2 gap-2">
                      {TARGET_POPULATIONS.map((pop) => (
                        <label key={pop} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.target_populations.includes(pop)}
                            onChange={() => handleMultiSelect('target_populations', pop)}
                            className="mr-2"
                          />
                          <span className="text-sm">{pop}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Certifications / Registrations
                    </label>
                    <textarea
                      name="certifications"
                      value={formData.certifications}
                      onChange={handleChange}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Registered with State Mental Health Authority"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Treatment Approaches</label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                      {TREATMENT_APPROACHES.map((approach) => (
                        <label key={approach} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.treatment_approaches.includes(approach)}
                            onChange={() => handleMultiSelect('treatment_approaches', approach)}
                            className="mr-2"
                          />
                          <span className="text-sm">{approach}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-6 border-t">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                data-testid="submit-service-button"
              >
                {loading ? 'Submitting...' : 'Submit for Approval'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/my-services')}
                className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterService;
